import intl from 'react-intl-universal';
import { store } from 'Store/store.index';
import { SEND_LINK_VIA_EMAIL } from 'components/viewer/ui/constants/event.constants.share';
import {
    SEND_FILE_AND_LINK_SUCCESS,
    SEND_FILE_AND_LINK_FAILED
} from 'Components/viewer/ui/constants/user.more.constants';
import { promise2ErrorFirst } from '../../dingtalk.util';
import { collaborationService } from '@/store/services';
import {
    collaborationApi,
    docCollaborationApi
} from '@/api';
import {
    COLLABORATION_FILE_UPLOAD_FINISH,
    COLLABORATION_FILE_UPLOAD_START
} from '@/constants/app.constant';
import DingtalkNotifyAPI from '../../apis/notify';
import { SendData } from './types';
import { ShareDocParams } from '../../dtos/notify';

export enum SendResult {
    Success = 1,
    FileSizeOver = 2,
    Fail = 3,
}

export async function send(sendData: SendData) {
    store.userStore.doSendGAEvent({ eventKey: SEND_LINK_VIA_EMAIL, label: 'share_files_dialog' } as any);
    store.collaborationStore.updateIsCreatingShareReview(true);
    const [err, data] = await promise2ErrorFirst(createShareAndInvite(sendData));
    store.collaborationStore.updateIsCreatingShareReview(false);
    if (err) {
        if (store.collaborationStore.isUploadedInitialAnnots) {
            store.collaborationStore.restoreAnnotsForCache();
        }
        if (err === 'fileSizeOver') return SendResult.FileSizeOver;
        return SendResult.Fail;
    }
    const { ret, data: shareData } = data as any;
    if (ret === 0) {
        store.userStore.doSendGAEventByUserEvent(SEND_FILE_AND_LINK_SUCCESS, null, null);
        return SendResult.Success;
    }
    store.userStore.doSendGAEventByUserEvent(SEND_FILE_AND_LINK_FAILED, null, null);
    switch (ret) {
        case 110002:
            store.collaborationStore.postErrorMsg({ type: 'error', errorInfo: intl.get('collaboration.Error.hasShared') });
            break;
        case 110015:
            store.collaborationStore.postErrorMsg({ type: 'error', errorInfo: intl.get("collaboration.Error.limitOnInvite", { limitNum: shareData[0].limitUserCount }) });
            break;
        case 110016:
            store.collaborationStore.postErrorMsg({ type: 'error', errorInfo: intl.get('collaboration.Error.noPermission') });
            break;
        default:
            store.collaborationStore.postErrorMsg({ type: 'error', errorInfo: intl.get('UnKnownError') });
            break;
    }
    return SendResult.Fail;
}

async function createShareAndInvite({ deadline, userIds }: SendData) {
    const isOverSize = await collaborationService.fileOverSizeCheck();
    if (isOverSize) throw 'fileSizeOver';
    // step 1, 上传 cpdf 文档数据并生成版本信息
    const result = await docCollaborationApi.getCDocInfo();
    if (result && result.ret !== 0) throw 'get cDocID error'
    await store.collaborationStore.updateAnnotsCache();
    let data = result && result.data;
    const { cDocID, docName } = data;
    // step 2, upload shareReview
    let { reviewSessionID, fileLink } = await updateShareReview(cDocID, docName, deadline);
    // step 3, update file
    if (!fileLink && !(fileLink = await doUpdateFile(reviewSessionID))) {
        store.collaborationStore.postErrorMsg({ type: 'error', errorInfo: intl.get('collaboration.sharedList.uploadFailed') });
        throw 'no fileLink';
    }
    collaborationService.dingtalkSetCollaborationData(cDocID, docName, fileLink, reviewSessionID);
    const params: ShareDocParams = {
        cDocID,
        reviewSessionID,
        fileLink,
        doc_name: docName,
        userList: userIds,
    }
    // step 4, invite to collaborate, and notify
    return await DingtalkNotifyAPI.shareDocAndNotify(params);
}

async function updateShareReview(cDocID, docName, deadline) {
    const shareViewSendData = {
        groups: ['anyone'],
        isAnonymous: 0,
        cDocID,
        docName,
        deadline,
    }
    const [err, shareViewResult] = await promise2ErrorFirst(collaborationApi.uploadShareReview(shareViewSendData));
    if (err) {
        store.collaborationStore.handleErrorMsg(err);
        throw err;
    }
    const { data, ret, message } = shareViewResult as any;

    if (ret === 0) {
        const { reviewSessionID, docName } = data.shareReview;
        store.collaborationStore.setReviewSessionID(reviewSessionID)
        store.collaborationStore.updateIsUploadedInitialAnnots(true)
        await collaborationService.uploadInitialAnnots();
        return {
            reviewSessionID,
            docName
        };
    }
    if (message === 'REVIEWSESSIONID_IS_EXIST' && ret === 2100023) {
        const { reviewSessionID } = data;
        store.collaborationStore.setReviewSessionID(reviewSessionID)
        const {
            data: { docName, fileLink } = { docName: null, fileLink: null }
        } = await collaborationApi.getShareReview({ cDocID, reviewSessionID } as any) as any;
        return {
            reviewSessionID,
            docName,
            fileLink
        };
    }
    throw shareViewResult;
}

async function doUpdateFile(reviewSessionID) {
    // const { ret, data: file } = await docCollaborationApi.getCurrentDoc();
    const result = await docCollaborationApi.getCurrentDoc();
    if ((result && result.ret !== 0) || (result && !result.file)) {
        return;
    }
    const file = result && result.file;
    store.appStore.sendPerformanceInfo({ event: COLLABORATION_FILE_UPLOAD_START, isEmit: false, lastEvent: true } as any);
    const fileLink = await collaborationService.doUploadFile({ file, reviewSessionID });
    store.appStore.sendPerformanceInfo({ event: COLLABORATION_FILE_UPLOAD_FINISH, isEmit: true, lastEvent: true } as any);
    return fileLink;
}
